export const stemInfo: Record<string, { pinyin: string; element: string; polarity: 'Yang' | 'Yin' }> = {
  '甲': { pinyin: 'Jia', element: 'wood', polarity: 'Yang' },
  '乙': { pinyin: 'Yi', element: 'wood', polarity: 'Yin' },
  '丙': { pinyin: 'Bing', element: 'fire', polarity: 'Yang' },
  '丁': { pinyin: 'Ding', element: 'fire', polarity: 'Yin' },
  '戊': { pinyin: 'Wu', element: 'earth', polarity: 'Yang' },
  '己': { pinyin: 'Ji', element: 'earth', polarity: 'Yin' },
  '庚': { pinyin: 'Geng', element: 'metal', polarity: 'Yang' },
  '辛': { pinyin: 'Xin', element: 'metal', polarity: 'Yin' },
  '壬': { pinyin: 'Ren', element: 'water', polarity: 'Yang' },
  '癸': { pinyin: 'Gui', element: 'water', polarity: 'Yin' },
};

export const branchInfo: Record<string, { pinyin: string; animal: string; animalIndo: string; element: string; polarity: 'Yang' | 'Yin' }> = {
  '子': { pinyin: 'Zi', animal: 'Rat', animalIndo: 'Tikus', element: 'water', polarity: 'Yang' },
  '丑': { pinyin: 'Chou', animal: 'Ox', animalIndo: 'Kerbau', element: 'earth', polarity: 'Yin' },
  '寅': { pinyin: 'Yin', animal: 'Tiger', animalIndo: 'Macan', element: 'wood', polarity: 'Yang' },
  '卯': { pinyin: 'Mao', animal: 'Rabbit', animalIndo: 'Kelinci', element: 'wood', polarity: 'Yin' },
  '辰': { pinyin: 'Chen', animal: 'Dragon', animalIndo: 'Naga', element: 'earth', polarity: 'Yang' },
  '巳': { pinyin: 'Si', animal: 'Snake', animalIndo: 'Ular', element: 'fire', polarity: 'Yin' },
  '午': { pinyin: 'Wu', animal: 'Horse', animalIndo: 'Kuda', element: 'fire', polarity: 'Yang' },
  '未': { pinyin: 'Wei', animal: 'Goat', animalIndo: 'Kambing', element: 'earth', polarity: 'Yin' },
  '申': { pinyin: 'Shen', animal: 'Monkey', animalIndo: 'Monyet', element: 'metal', polarity: 'Yang' },
  '酉': { pinyin: 'You', animal: 'Rooster', animalIndo: 'Ayam', element: 'metal', polarity: 'Yin' },
  '戌': { pinyin: 'Xu', animal: 'Dog', animalIndo: 'Anjing', element: 'earth', polarity: 'Yang' },
  '亥': { pinyin: 'Hai', animal: 'Pig', animalIndo: 'Babi', element: 'water', polarity: 'Yin' },
};

export const branchHiddenStems: Record<string, string[]> = {
  '子': ['癸'],
  '丑': ['癸', '辛', '己'],
  '寅': ['戊', '丙', '甲'],
  '卯': ['乙'],
  '辰': ['癸', '戊', '乙'],
  '巳': ['庚', '丙', '戊'],
  '午': ['己', '丁'],
  '未': ['乙', '己', '丁'],
  '申': ['戊', '壬', '庚'],
  '酉': ['辛'],
  '戌': ['辛', '戊', '丁'],
  '亥': ['甲', '壬'],
};

export const computeTenGod = (dmStem: string, targetStem: string): { zhFull: string; zhAbbr: string; en: string } => {
  const dm = stemInfo[dmStem];
  const target = stemInfo[targetStem];
  if (!dm || !target) return { zhFull: '', zhAbbr: '', en: '' };

  const dmEl = dm.element;
  const targetEl = target.element;
  const samePolarity = dm.polarity === target.polarity;

  if (dmEl === targetEl) {
    return samePolarity 
      ? { zhFull: '比肩', zhAbbr: '比', en: 'Fr' } 
      : { zhFull: '劫財', zhAbbr: '劫', en: 'RW' };
  }

  const produces: Record<string, string> = { wood: 'fire', fire: 'earth', earth: 'metal', metal: 'water', water: 'wood' };
  if (produces[dmEl] === targetEl) {
    return samePolarity 
      ? { zhFull: '食神', zhAbbr: '食', en: 'EG' } 
      : { zhFull: '傷官', zhAbbr: '傷', en: 'HO' };
  }

  const controls: Record<string, string> = { wood: 'earth', earth: 'water', water: 'fire', fire: 'metal', metal: 'wood' };
  if (controls[dmEl] === targetEl) {
    return samePolarity 
      ? { zhFull: '偏財', zhAbbr: '才', en: 'IW' } 
      : { zhFull: '正財', zhAbbr: '财', en: 'DW' };
  }

  if (controls[targetEl] === dmEl) {
    return samePolarity 
      ? { zhFull: '七殺', zhAbbr: '殺', en: 'QS' } 
      : { zhFull: '正官', zhAbbr: '官', en: 'DO' };
  }

  if (produces[targetEl] === dmEl) {
    return samePolarity 
      ? { zhFull: '偏印', zhAbbr: '卩', en: 'IR' } 
      : { zhFull: '正印', zhAbbr: '印', en: 'DR' };
  }

  return { zhFull: '', zhAbbr: '', en: '' };
};

export const getNaYinElement = (stem: string, branch: string): { element: string; elementCh: string; name: string } => {
  const stemValues: Record<string, number> = { '甲': 1, '乙': 1, '丙': 2, '丁': 2, '戊': 3, '己': 3, '庚': 4, '辛': 4, '壬': 5, '癸': 5 };
  const branchValues: Record<string, number> = { '子': 1, '丑': 1, '午': 1, '未': 1, '寅': 2, '卯': 2, '申': 2, '酉': 2, '辰': 3, '巳': 3, '戌': 3, '亥': 3 };

  const sVal = stemValues[stem] || 0;
  const bVal = branchValues[branch] || 0;
  if (!sVal || !bVal) return { element: 'water', elementCh: '水', name: 'Water' };

  let sum = sVal + bVal;
  if (sum > 5) sum -= 5;

  const elements: Record<number, { element: string; elementCh: string; nameEn: string }> = {
    1: { element: 'wood', elementCh: '木', nameEn: 'Wood' },
    2: { element: 'metal', elementCh: '金', nameEn: 'Metal' },
    3: { element: 'water', elementCh: '水', nameEn: 'Water' },
    4: { element: 'fire', elementCh: '火', nameEn: 'Fire' },
    5: { element: 'earth', elementCh: '土', nameEn: 'Earth' }
  };

  const res = elements[sum] || { element: 'water', elementCh: '水', nameEn: 'Water' };
  return { element: res.element, elementCh: res.elementCh, name: res.nameEn };
};

export const getQiPhase = (dmStem: string, targetBranch: string): string => {
  const branches = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
  const stages = ['Birth', 'Bath', 'Youth', 'Officer', 'Peak', 'Decay', 'Sickness', 'Death', 'Tomb', 'Extinction', 'Embryo', 'Nourishment'];
  const starts: Record<string, { startBranch: string; direction: 1 | -1 }> = {
    '甲': { startBranch: '亥', direction: 1 },
    '乙': { startBranch: '午', direction: -1 },
    '丙': { startBranch: '寅', direction: 1 },
    '丁': { startBranch: '酉', direction: -1 },
    '戊': { startBranch: '寅', direction: 1 },
    '己': { startBranch: '酉', direction: -1 },
    '庚': { startBranch: '巳', direction: 1 },
    '辛': { startBranch: '子', direction: -1 },
    '壬': { startBranch: '申', direction: 1 },
    '癸': { startBranch: '卯', direction: -1 }
  };

  const config = starts[dmStem];
  if (!config) return '';

  const startIndex = branches.indexOf(config.startBranch);
  const targetIndex = branches.indexOf(targetBranch);
  if (startIndex === -1 || targetIndex === -1) return '';

  let diff = config.direction === 1 ? (targetIndex - startIndex + 12) % 12 : (startIndex - targetIndex + 12) % 12;
  return stages[diff];
};

export const qiPhaseMap: Record<string, { zh: string; id: string }> = {
  'Birth': { zh: '长生', id: 'Kelahiran (Birth)' },
  'Bath': { zh: '沐浴', id: 'Mandi (Bath)' },
  'Youth': { zh: '冠带', id: 'Masa Muda (Youth)' },
  'Officer': { zh: '临官', id: 'Kematangan (Officer)' },
  'Peak': { zh: '帝旺', id: 'Puncak (Peak)' },
  'Decay': { zh: '衰', id: 'Kemunduran (Decay)' },
  'Sickness': { zh: '病', id: 'Sakit (Sickness)' },
  'Death': { zh: '死', id: 'Kematian (Death)' },
  'Tomb': { zh: '墓', id: 'Kubur (Tomb)' },
  'Extinction': { zh: '绝', id: 'Kepunahan (Extinction)' },
  'Embryo': { zh: '胎', id: 'Kandungan (Embryo)' },
  'Nourishment': { zh: '养', id: 'Asuhan (Nourishment)' }
};
